import { loadCalendarEventsPage } from "@data/eventsDataQuery"
import OptimizedImage from "@shared/ui/OptimizedImage"
import {
  CALENDAR_EVENTS_PAGE_SIZE,
  type CalendarEvent,
  type CalendarEventsPageInput,
  type CalendarEventsResponse,
} from "@site-types/calendar"
import { type CSSProperties, useCallback, useEffect, useRef, useState } from "react"

const EVENT_CARD_CLASS =
  "event flex w-full translate-y-0 items-center justify-center bg-[var(--white)] p-0 leading-none shadow-[8px_8px_0_var(--blue-50)] transition-[scale,box-shadow] duration-100 ease-out [grid-row-end:span_4]"

function taiyoroEventUrl(id: string) {
  return `https://taiyoro.gg/event/${encodeURIComponent(id)}`
}

function eventPlacementClassName(index: number) {
  return [
    index % 2 === 0 ? "col-start-2" : "col-start-1",
    index === 0 ? "row-start-1" : "",
    index === 1 ? "row-start-2" : "",
    index % 3 === 0 ? "calendar:col-start-3" : "",
    index % 3 === 1 ? "calendar:col-start-2" : "",
    index % 3 === 2 ? "calendar:col-start-1" : "",
    index === 2 ? "calendar:row-start-3" : "",
    index % 4 === 0 ? "calendar-wide:col-start-4" : "",
    index % 4 === 1 ? "calendar-wide:col-start-3" : "",
    index % 4 === 2 ? "calendar-wide:col-start-2" : "",
    index % 4 === 3 ? "calendar-wide:col-start-1" : "",
    index === 3 ? "calendar-wide:row-start-4" : "",
  ]
    .filter(Boolean)
    .join(" ")
}

type EventsProps = CalendarEventsResponse & {
  loadPage?: (
    input: Required<Pick<CalendarEventsPageInput, "limit" | "offset">>
  ) => Promise<CalendarEventsResponse>
}

const Events = ({
  hasMore: initialHasMore = false,
  loadPage = loadCalendarEventsPage,
  nextOffset,
  pageSize = CALENDAR_EVENTS_PAGE_SIZE,
  results,
}: EventsProps) => {
  const [events, setEvents] = useState<CalendarEvent[]>(results)
  const [hasMore, setHasMore] = useState(initialHasMore)
  const [offset, setOffset] = useState(nextOffset ?? results.length)
  const [isLoading, setIsLoading] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [animateFrom, setAnimateFrom] = useState(0)
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const loadingRef = useRef(false)

  useEffect(() => {
    const shipElement = document.querySelector("#ship")
    if (shipElement) {
      shipElement.classList.remove("place-center")
      shipElement.classList.add("place-back")
    }
  }, [])

  useEffect(() => {
    setEvents(results)
    setHasMore(initialHasMore)
    setOffset(nextOffset ?? results.length)
    setAnimateFrom(0)
  }, [initialHasMore, nextOffset, results])

  const loadNextPage = useCallback(async () => {
    if (!hasMore || loadingRef.current) return

    loadingRef.current = true
    setIsLoading(true)
    setLoadFailed(false)

    try {
      const page = await loadPage({ limit: pageSize, offset })
      setEvents((currentEvents) => {
        setAnimateFrom(currentEvents.length)
        const currentIds = new Set(currentEvents.map((event) => event.id))
        const newEvents = page.results.filter((event) => !currentIds.has(event.id))
        return [...currentEvents, ...newEvents]
      })
      setHasMore(Boolean(page.hasMore))
      setOffset(page.nextOffset ?? offset + page.results.length)
    } catch {
      setLoadFailed(true)
    } finally {
      loadingRef.current = false
      setIsLoading(false)
    }
  }, [hasMore, loadPage, offset, pageSize])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!hasMore || !sentinel || typeof IntersectionObserver === "undefined") return undefined

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) void loadNextPage()
      },
      { rootMargin: "360px 0px" }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, loadNextPage])

  return (
    <>
      <div
        data-id="events-list"
        data-total-count={events.length}
        className="mx-auto my-[20px] grid w-[calc(100%_-_40px)] max-w-[1300px] calendar-wide:grid-cols-4 calendar:grid-cols-3 grid-cols-2 gap-y-[20px] calendar-wide:pb-[80px] text-center [column-gap:5%] calendar-narrow:[column-gap:20px]"
      >
        {events.length > 0 &&
          events.map((event, index) => (
            <div
              className={`${EVENT_CARD_CLASS} [animation:slideUp_0.4s_cubic-bezier(0.19,1,0.22,1)_calc(var(--i,0)*100ms)_both] hover:scale-[0.98] hover:cursor-pointer hover:shadow-[4px_4px_0_var(--blue-50)] active:scale-[1.02] active:shadow-[8px_10px_0_var(--blue-50)] [&.hit-shake]:[animation:hitShake_0.4s_cubic-bezier(0.36,0.07,0.19,0.97)_both] ${eventPlacementClassName(index)}`}
              data-id={`event-${index}`}
              data-event-id={event.id}
              style={{ "--i": Math.max(0, index - animateFrom) } as CSSProperties}
              data-order={index}
              key={`event_${event.id}`}
            >
              <a
                href={taiyoroEventUrl(event.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="aspect-video w-full bg-[var(--green)]"
              >
                <OptimizedImage
                  draggable={false}
                  src={event.imageUrl || event?.games?.[0]?.primaryImage}
                  alt={event.name}
                  width={640}
                  height={360}
                  className="block h-full w-full bg-[var(--pink)] text-[var(--white)]"
                />
              </a>
            </div>
          ))}
      </div>
      {hasMore && (
        <div
          aria-busy={isLoading}
          className="mx-auto h-20 w-full max-w-[1300px]"
          data-id="events-load-sentinel"
          ref={sentinelRef}
        >
          <div className="mx-auto mt-6 h-2 w-24 overflow-hidden bg-[var(--white)] opacity-70 shadow-[4px_4px_0_var(--blue-50)]">
            <div
              className="h-full w-full origin-left bg-[var(--green)] [animation:loadingBar_900ms_ease-in-out_infinite]"
              data-id="events-load-indicator"
            />
          </div>
        </div>
      )}
      {loadFailed && (
        <button
          className="mx-auto mb-10 block border-0 bg-[var(--white)] px-5 py-3 font-bold text-[var(--deep-blue)] shadow-[4px_4px_0_var(--blue-50)] hover:cursor-pointer hover:bg-[var(--green)]"
          data-id="events-load-retry"
          onClick={() => void loadNextPage()}
          type="button"
        >
          Retry
        </button>
      )}
    </>
  )
}

export default Events
